// Using native fetch like other services in the codebase
import { DAY_DISPLAY_NAMES, DEFAULT_REST_SECONDS, type DayOfWeek } from '../lib/constants'
import type { SplitExercise } from '../stores/splitConfigStore'

export interface HevyFolder {
  id: number
  title: string
  updated_at: string
  created_at: string
}

export interface HevyCreateFolderRequest {
  routine_folder: {
    title: string
  }
}

export interface HevyCreateFolderResponse {
  routine_folder: HevyFolder
}

export interface HevyRoutineExercise {
  exercise_template_id: string
  superset_id?: string | null
  rest_seconds: number
  notes?: string
  sets: HevyRoutineSet[]
}

export interface HevyRoutineSet {
  type: 'normal' | 'warmup' | 'failure' | 'dropset'
  weight_kg?: number | null
  reps?: number | null
  distance_meters?: number | null
  duration_seconds?: number | null
  custom_metric?: Record<string, unknown> | null
}

export interface HevyCreateRoutineRequest {
  routine: {
    title: string
    folder_id?: number | null
    notes?: string
    exercises: HevyRoutineExercise[]
  }
}

export interface HevyCreateRoutineResponse {
  routine: {
    id: string
    title: string
    folder_id?: number
    exercises: HevyRoutineExercise[]
    updated_at: string
    created_at: string
  }
}

export interface PushSplitOptions {
  splitName: string
  routines: Record<string, { id: string; name: string; exercises: SplitExercise[] }>
  dayAssignments: Record<DayOfWeek, string | null>
  cycleMode?: boolean
  cycleAssignments?: (string | null)[]
  createFolder?: boolean
  folderName?: string
}

export interface PushSplitResult {
  success: boolean
  folder?: HevyFolder
  routines: Array<{
    day: DayOfWeek
    routine?: HevyCreateRoutineResponse['routine']
    error?: string
  }>
  uniqueRoutinesCreated: number
  errors: string[]
}

/**
 * Create a folder in Hevy
 */
export async function createHevyFolder(
  apiKey: string,
  folderTitle: string
): Promise<HevyFolder> {
  try {
    const response = await fetch('https://api.hevyapp.com/v1/routine_folders', {
      method: 'POST',
      headers: {
        'api-key': apiKey,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        routine_folder: {
          title: folderTitle
        }
      })
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    const data: HevyCreateFolderResponse = await response.json()
    return data.routine_folder
  } catch (error) {
    console.error('Failed to create folder:', error)
    throw new Error(`Failed to create folder: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

/**
 * Create a routine in Hevy
 */
export async function createHevyRoutine(
  apiKey: string,
  title: string,
  exercises: HevyRoutineExercise[],
  folderId?: number,
  notes?: string
): Promise<HevyCreateRoutineResponse['routine']> {
  try {
    const payload: HevyCreateRoutineRequest = {
      routine: {
        title,
        folder_id: folderId || null,
        notes: notes || "",
        exercises
      }
    }
    
    // Debug log the payload (only in development)
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('Creating routine with payload:', JSON.stringify(payload, null, 2))
    }
    
    const response = await fetch('https://api.hevyapp.com/v1/routines', {
      method: 'POST',
      headers: {
        'api-key': apiKey,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      let errorDetails = `HTTP ${response.status}: ${response.statusText}`
      try {
        const errorBody = await response.text()
        console.error('Hevy API error response:', errorBody)
        errorDetails += ` - ${errorBody}`
      } catch (e) {
        // Ignore if we can't read the error body
      }
      throw new Error(errorDetails)
    }

    const data: HevyCreateRoutineResponse = await response.json()
    return data.routine
  } catch (error) {
    console.error('Failed to create routine:', error)
    throw new Error(`Failed to create routine: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

/**
 * Convert split exercise to Hevy routine exercise format
 */
function convertToHevyExercise(exercise: SplitExercise): HevyRoutineExercise {
  // Convert split exercise sets to Hevy format
  const sets: HevyRoutineSet[] = exercise.sets.map(set => ({
    type: set.type || 'normal',
    weight_kg: set.weight,
    reps: set.reps,
    distance_meters: null,
    duration_seconds: null,
    custom_metric: null
  }))

  return {
    exercise_template_id: exercise.id,
    superset_id: null,
    rest_seconds: DEFAULT_REST_SECONDS,
    sets
  }
}

/**
 * Convert day name to display format
 */
function formatDayName(day: DayOfWeek, splitName: string): string {
  return `${splitName} - ${DAY_DISPLAY_NAMES[day]}`
}

/**
 * Push an entire split to Hevy with comprehensive procedure:
 * 1. Create folder if requested
 * 2. Wait for folder response and get ID
 * 3. Promise.all queue all routine POST requests into the folder
 */
export async function pushSplitToHevy(
  apiKey: string,
  options: PushSplitOptions
): Promise<PushSplitResult> {
    const result: PushSplitResult = {
      success: false,
      routines: [],
      uniqueRoutinesCreated: 0,
      errors: []
    }

    try {
      // Step 1: Create folder if requested and wait for response
      let folderId: number | undefined
      if (options.createFolder && options.folderName) {
        try {
          const folder = await createHevyFolder(apiKey, options.folderName)
          result.folder = folder
          folderId = folder.id
        } catch (error) {
          const errorMsg = `Failed to create folder: ${error instanceof Error ? error.message : 'Unknown error'}`
          result.errors.push(errorMsg)
          throw error // Stop execution if folder creation fails and was requested
        }
      }

      // Step 2: Group days by unique routines to avoid duplicates
      const uniqueRoutineMap = new Map<string, {
        routine: typeof options.routines[string]
        days: (DayOfWeek | number)[]
      }>()

      if (options.cycleMode && options.cycleAssignments) {
        // Cycle mode - process cycle days
        options.cycleAssignments.forEach((routineId, dayIndex) => {
          // Skip days with no routine assigned
          if (!routineId || !options.routines[routineId]) {
            return
          }

          const routine = options.routines[routineId]
          
          // Skip routines with no exercises
          if (!routine.exercises || routine.exercises.length === 0) {
            return
          }

          if (uniqueRoutineMap.has(routineId)) {
            // Add this day to existing routine
            uniqueRoutineMap.get(routineId)!.days.push(dayIndex + 1) // Day 1, 2, 3, etc.
          } else {
            // Create new entry for this routine
            uniqueRoutineMap.set(routineId, {
              routine,
              days: [dayIndex + 1]
            })
          }
        })
      } else {
        // Normal mode - process weekdays
        const dayOrder: DayOfWeek[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
        for (const day of dayOrder) {
          const routineId = options.dayAssignments[day]
          
          // Skip days with no routine assigned
          if (!routineId || !options.routines[routineId]) {
            continue
          }

          const routine = options.routines[routineId]
          
          // Skip routines with no exercises
          if (!routine.exercises || routine.exercises.length === 0) {
            continue
          }

          if (uniqueRoutineMap.has(routineId)) {
            // Add this day to existing routine
            uniqueRoutineMap.get(routineId)!.days.push(day)
          } else {
            // Create new entry for this routine
            uniqueRoutineMap.set(routineId, {
              routine,
              days: [day]
            })
          }
        }
      }

      // Step 3: Create unique routine creation tasks
      const routineCreationTasks: Array<{
        routineId: string
        days: (DayOfWeek | number)[]
        routineName: string
        promise: Promise<HevyCreateRoutineResponse['routine']>
      }> = []

      for (const [routineId, { routine, days }] of uniqueRoutineMap.entries()) {
        // Convert exercises to Hevy format
        const hevyExercises = routine.exercises
          .sort((a, b) => a.order - b.order)
          .map(exercise => convertToHevyExercise(exercise))

        // Create routine name with day annotations
        let routineName: string
        
        if (options.cycleMode) {
          // Cycle mode - use Day 1, 2, 3 format
          const dayNumbers = (days as number[]).join(', ')
          routineName = `${routine.name || 'Routine'} (${dayNumbers})`
        } else {
          // Normal mode - use weekday names
          const dayNames = (days as DayOfWeek[]).map(day => formatDayName(day, '').replace(' - ', '')).join(', ')
          
          if (routine.name && routine.name.trim()) {
            // Use custom name with day annotation
            routineName = `${routine.name} (${dayNames})`
          } else {
            // Use default name pattern
            if (days.length === 1) {
              routineName = `${formatDayName(days[0] as DayOfWeek, options.splitName)} Workout`
            } else {
              routineName = `${options.splitName} - ${routine.name || 'Routine'} (${dayNames})`
            }
          }
        }
        
        // Create routine creation promise
        const routinePromise = createHevyRoutine(
          apiKey,
          routineName,
          hevyExercises,
          folderId,
          ""
        )

        routineCreationTasks.push({
          routineId,
          days,
          routineName,
          promise: routinePromise
        })
      }

      // Step 4: Execute all routine creation requests in parallel using Promise.allSettled
      const routineResults = await Promise.allSettled(
        routineCreationTasks.map(task => 
          task.promise.then(routine => ({ 
            routineId: task.routineId, 
            days: task.days, 
            routine 
          }))
        )
      )

      // Step 5: Process results and map back to days
      let successfulUniqueRoutines = 0
      
      routineResults.forEach((routineResult, index) => {
        const { days } = routineCreationTasks[index]
        
        if (routineResult.status === 'fulfilled') {
          // Success - add result for each day this routine is used
          successfulUniqueRoutines++
          if (options.cycleMode) {
            // For cycle mode, we don't map back to specific days
            // Just track that the routine was created successfully
            result.routines.push({
              day: 'monday' as DayOfWeek, // Placeholder since we need a day
              routine: routineResult.value.routine
            })
          } else {
            days.forEach(day => {
              result.routines.push({
                day: day as DayOfWeek,
                routine: routineResult.value.routine
              })
            })
          }
        } else {
          // Error - add error for each day this routine is used
          const dayDescription = options.cycleMode 
            ? `Day ${days.join(', ')}` 
            : days.join(', ')
          const errorMsg = `Failed to create routine for ${dayDescription}: ${routineResult.reason instanceof Error ? routineResult.reason.message : 'Unknown error'}`
          
          if (options.cycleMode) {
            result.routines.push({
              day: 'monday' as DayOfWeek, // Placeholder
              error: errorMsg
            })
          } else {
            days.forEach(day => {
              result.routines.push({
                day: day as DayOfWeek,
                error: errorMsg
              })
            })
          }
          result.errors.push(errorMsg)
        }
      })

      // Set the count of unique routines successfully created
      result.uniqueRoutinesCreated = successfulUniqueRoutines
      result.success = successfulUniqueRoutines > 0

      return result
    } catch (error) {
      const errorMsg = `Failed to push split: ${error instanceof Error ? error.message : 'Unknown error'}`
      result.errors.push(errorMsg)
      return result
    }
}